<template lang="pug">
v-container.pa-8(fluid)
  v-row.pb-2.pb-md-0(no-gutters)
    //- Top Tabs
    template(v-if="selectedPatients.length")
      v-col(cols="12" md="6")
        .d-flex.align-center.ml-5.mt-2.pb-1
          v-btn.button-Aa-small.mr-3.px-3(v-if="segmentTab === 0" @click="onDelete" color="danger500" height=32 outlined depressed) {{ $t('delete') }}
          v-btn.button-Aa-small.mr-3.px-3(v-else-if="!segments[segmentTab].dynamic" color="danger500" height=32 outlined depressed @click="openSegmentRemovePatientDialog = true") Remove from segment
          v-btn.button-Aa-small.mr-3.px-3(v-if="!segments[segmentTab].dynamic" @click="onAddSegment" color="primary" outlined height=32 depressed) {{ $t('add_to_segment') }}
          v-btn.button-Aa-small.mr-3.px-3(@click="onSendSms" color="primary" outlined height=32 depressed) {{ $t('send_sms') }}
          v-btn.button-Aa-small.mr-3.px-3(@click="onSendEmail" color="primary" outlined height=32 depressed) {{ $t('send_email') }}
          a.link-s(@click="clearSelection") {{ $t('clear_selection') }}
    template(v-else)
      v-col(cols="12" md="10")
        div.d-flex.align-center.my-5(v-if="isFetchingSegment")
          v-progress-linear(:width="3" rounded color="primary" indeterminate)
        v-tabs(v-else v-model="segmentTab" color="basic700" :height="44" :grow="isMobileScreen" show-arrows active-class="font-semibold" @change="tabChanged")
          v-tab.px-0(v-for="segment in segments" :key="segment.id")
            div(v-if="tabShowMenu(segment)")
              v-menu(v-if="showMenu" offset-y rounded="lg")
                template(#activator="{ on }")
                  v-btn.text-sm.font-medium.text-none(text small v-on="on") {{ segment.title }} ({{ segment.customer_count }})
                    v-icon(right size="20" color="neutral700") $yzi-chevron-down
                v-list.py-0
                  v-list-item(@click="openEditSegmentDialog = true")
                    v-list-item-title
                      div.d-flex.align-center
                        v-icon.mr-2(color="neutral700" size="18") $yzi-text-input
                        div.text-sm.neutral700--text Rename
                  template(v-if="segment.dynamic")
                    v-divider
                    v-list-item(@click="openDynamicFilter")
                      v-list-item-title
                        div.d-flex.align-center
                          v-icon.mr-2(color="neutral700" size="18") $yzi-data-flow-01
                          div.text-sm.neutral700--text Edit dynamic filters
                  template(v-else)
                    v-divider
                    v-list-item(@click="openCustomerSegmentDialog = true")
                      v-list-item-title
                        div.d-flex.align-center
                          v-icon.mr-2(color="neutral700" size="18") $yzi-users-plus
                          div.text-sm.neutral700--text Add customers
                  v-divider
                  v-list-item(@click="openDuplicateSegmentDialog = true")
                    v-list-item-title
                      div.d-flex.align-center
                        v-icon.mr-2(color="neutral700" size="18") $yzi-copy-03
                        div.text-sm.neutral700--text Duplicate
                  v-divider
                  v-list-item(@click="openDeleteSegmentDialog = true")
                    v-list-item-title
                      div.d-flex.align-center
                        v-icon.mr-2(color="danger500" size="18") $yzi-trash-03
                        div.text-sm.danger500--text Delete

              div.text-sm.font-medium.text-none(v-else style="padding: 0 26px 0 26px;") {{ segment.title }} ({{ segment.customer_count }})
            div.text-sm.font-medium.text-none.px-4(v-else) {{ segment.title }}
              span.ml-1(v-if="segment.id !== 'all'") ({{ segment.customer_count }})
          v-btn.my-auto(icon small color="transparent" @click="openAddSegmentDialog = true")
            v-icon(color="neutral700" size="23") mdi-plus

    v-col(cols="12" :md="selectedPatients.length ? 6 : 2" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(v-if="!showSearchField" icon @click="openAddSegmentDialog = true")
          v-icon(color="neutral700" size="23") mdi-plus
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat :placeholder="$t('type_to_search')" hide-details clearable autofocus)
        div(v-if="!showSearchField")
          customize-column-menu(type="patient" :showTooltip="false" iconSize="21")

  //- Filter
  dynamic-filter-row(v-if="showDynamicFilters" :segment.sync="selectedSegment" @updated="updateDynamicFilter" @closed="showDynamicFilters = false")
  patients-filter-row.my-4(v-else ref="filter" :filters.sync="filters" :prop-filters="storedFilters")
  .data-table
    v-data-table.mt-sm-4.pb-6(:headers="headers" :items="customers" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
      template(v-slot:no-data)
        div.mt-16(v-if="selectedSegment && selectedSegment.dynamic" )
          v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
          .mt-2.p2.basic600--text {{ $t('no_list_available') }}
        div.empty-box.mt-6.pa-8(v-else-if="!isFetching && !isFetchingSegment && !filters.length" align="center")
          v-avatar(color="neutral300" size="54")
            v-icon(color="neutral500" size="36") $yzi-users-02
          div.text-center.text-md.font-semibold.neutral700--text.mt-6 Start adding customers into this segment
          y-button.mt-4(label="Add customer" prepend-icon="$yzi-plus" size="sm" @click="openCustomerSegmentDialog = true")
      template(v-slot:header.checked)
        yc-checkbox(v-model="checkAll" @change="onCheckedAll")
      template(v-slot:header.phone)
        span {{ 'phone' | fieldNameDisplay }}
      template(v-slot:header.total_paid)
        span {{ 'total_paid' | fieldNameDisplay }}
      template(v-slot:header.marital_status)
        span {{ 'marital_status' | fieldNameDisplay }}
      template(v-slot:item="{ item, isMobile, headers, index }")
        tr.primary--text
          template(v-for="header in headers" )
            td(@click="clickedRow(item)")
              template(v-if="header.value === 'checked'")
                yc-checkbox(v-model="item.checked" @change="onChecked")
              template(v-else-if="header.value === 'name'")
                div.d-flex.my-3(style="max-width: 200px;")
                  div(style="width: 30px;")
                    v-responsive(:aspect-ratio="7/10" style="width: 30px;")
                      v-card.fill-height(v-if="item.profile_image && item.profile_image.file" flat)
                        v-img(:src="`https://img.yezza.io/50x50/${item.profile_image.path}`" height="100%" width="100%")
                          template(v-slot:placeholder)
                            v-row(class="fill-height ma-0" align="center" justify="center")
                              v-progress-circular(indeterminate color="grey")
                      v-card.neutral300.fill-height(v-else flat)
                        div.d-flex.align-center.justify-center.fill-height
                          v-icon(size="20") $yzi-user-03
                  div.pl-2
                    v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                      template(#activator="{ on, attrs }")
                        div(v-bind="attrs" v-on="on" style="max-width: 145px;")
                          .text-sm.font-semibold.neutral700--text.cursor-help.truncate {{ item.name }}
                      div(style="max-width: 600px;")
                        .text-sm.font-semibold {{ item.name }}
                        .text-xs(v-if="item.dob") {{ $options.filters.getAgeByYearMonthDay(item.dob) | displayAgeByYearMonthDay }} old
                    div.d-flex.align-center
                      y-chip.mr-2(v-if="item.urgent" label="Urgent" size="xs" color="red")
                      .text-xs.neutral700--text.no-wrap(v-if="item.dob") {{ $options.filters.getAgeByYearMonthDay(item.dob) | displayAgeByYearMonthDay }} old
              template(v-else-if="header.value === 'total_paid'")
                .div {{ item.total_paid | displayPrice }}
              template(v-else-if="header.value === 'created'")
                div.text-xs.font-bold {{ (item.record_date || item.created) | humanizeDate }}
                div.text-xs {{ (item.record_date || item.created) | displayTime2 }}
              template(v-else-if="header.value === 'gender'")
                div {{ item.gender | capitalize }}
              template(v-else-if="header.value === 'marital_status'")
                div {{ item.marital_status | capitalize }}
              template(v-else-if="header.value === 'address'")
                div(v-if="item.address")
                  v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                    template(#activator="{ on, attrs }")
                      .text-sm.neutral700--text.cursor-help(v-if="item.address.city" v-bind="attrs" v-on="on") {{ item.address.city }}
                    div.text-xs(style="max-width: 250px;") {{ getAddressInString(item.address) }}
              template(v-else)
                div {{ getFieldData(header.value, item) }}

  div.d-flex.align-center(v-if="totalCount > pageSize")
    v-spacer
    yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  .dialogs
    add-segment-dialog(v-if="openAddSegmentDialog" :open.sync="openAddSegmentDialog" @updated="fetchSegments")
    add-to-segment-dialog(v-if="openAddToSegmentDialog" :open.sync="openAddToSegmentDialog" :customers="selectedPatients" @addNewSegment="fetchSegments" @success="successAddCustomer")
    edit-segment-dialog(v-if="openEditSegmentDialog" :open.sync="openEditSegmentDialog" :segment="selectedSegment" @updated="fetchSegments")
    duplicate-segment-dialog(v-if="openDuplicateSegmentDialog" :open.sync="openDuplicateSegmentDialog" :segment="selectedSegment" @updated="fetchSegments")
    customer-segment-dialog(v-if="openCustomerSegmentDialog" :open.sync="openCustomerSegmentDialog" :segment="selectedSegment" @updated="reset")
    delete-customer-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :customers="selectedPatients" @deleted="successDeleteCustomer")
    delete-segment-dialog(v-if="openDeleteSegmentDialog" :open.sync="openDeleteSegmentDialog" :segment="selectedSegment" @deleted="fetchSegments")
    segment-remove-patient-dialog(v-if="openSegmentRemovePatientDialog" :open.sync="openSegmentRemovePatientDialog" :segment="selectedSegment" :customers="selectedPatients" @deleted="successRemoveSegmentCustomer")

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import PatientsFilterRow from '@/components/clinic/patient/filters/PatientsFilterRow.vue'
import DynamicFilterRow from '@/components/clinic/patient/filters/DynamicFilterRow.vue'
import AddSegmentDialog from '@/components/clinic/patient/AddSegmentDialog.vue'
import AddToSegmentDialog from '@/components/clinic/patient/AddToSegmentDialog.vue'
import EditSegmentDialog from '@/components/clinic/patient/EditSegmentDialog.vue'
import DuplicateSegmentDialog from '@/components/clinic/patient/DuplicateSegmentDialog.vue'
import SegmentRemovePatientDialog from '@/components/clinic/dialogs/SegmentRemovePatientDialog.vue'
import CustomerSegmentDialog from '@/components/clinic/patient/CustomerSegmentDialog.vue'
import DeleteCustomerDialog from '@/components/connect/dialogs/DeleteCustomerDialog.vue'
import DeleteSegmentDialog from '@/components/clinic/patient/DeleteSegmentDialog.vue'

export default {
  components: {
    CustomizeColumnMenu,
    DeleteCustomerDialog,
    AddToSegmentDialog,
    AddSegmentDialog,
    EditSegmentDialog,
    DuplicateSegmentDialog,
    SegmentRemovePatientDialog,
    PatientsFilterRow,
    DynamicFilterRow,
    DeleteSegmentDialog,
    CustomerSegmentDialog
  },
  data: () => ({
    isLoading: false,
    isFetching: false,
    isFetchingSegment: true,
    openDeleteDialog: false,
    openDeleteSegmentDialog: false,
    openAddToSegmentDialog: false,
    openAddSegmentDialog: false,
    openEditSegmentDialog: false,
    openDuplicateSegmentDialog: false,
    openSegmentRemovePatientDialog: false,
    openCustomerSegmentDialog: false,
    showDynamicFilters: false,
    showSearchField: false,
    showMenu: false,
    checkAll: false,
    selectedPatient: null,
    selectedAddContactOption: null,
    selectedSegment: null,
    pageSize: 20,
    totalCount: 0,
    customers: [],
    selectedPatients: [],
    filters: [],
    addContactOptions: [
      { 
        id: 'single', 
        title: 'Add single patient', 
        description: 'Manually add a contact by filling up required field' 
      },
      { 
        id: 'csv', 
        title: 'Upload CSV', 
        description: 'Import your patients by uploading the completed csv. file template' 
      }
    ],
    activeStyle: {
      border: '1.5px solid #0C6DFF',
      borderRadius: '4px',
      padding: '16px',
      cursor: 'pointer'
    },
    inactiveStyle: {
      border: '1px solid var(--v-basic400)',
      borderRadius: '4px',
      padding: '16px',
      cursor: 'pointer'
    },
    segments: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      fields: 'clinic/getPatientFields',
      storedTabs: 'marketing/getCustomersTabs',
      storedPage: 'marketing/getCustomersPage',
      storedSearch: 'marketing/getCustomersSearch',
      storedFilters: 'marketing/getCustomersFilters',
    }),
    headers () {
      const filteredOptions = this.fields.filter(item => item.enable)
      const checkbox = {
        text: '',
        value: 'checked',
        sortable: false
      }
      const name = {
        text: 'Name',
        value: 'name',
        class: 'text-table-header'
      }
      filteredOptions.map(item => {
        item.text = item.field_name
        item.value = `${item.id}`.toLowerCase()
        item['class'] = 'text-table-header'
      })
      return [checkbox, name, ...filteredOptions]
    },
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    segmentTab: {
      get () { return this.storedTabs },
      set (val) { this.$store.commit('marketing/setCustomersTabs', val) }
    },
    currentPage: {
      get () { return this.storedPage },
      set (val) { this.$store.commit('marketing/setCustomersPage', val) }
    },
    searchText: {
      get () { return this.storedSearch },
      set (val) { this.$store.commit('marketing/setCustomersSearch', val) }
    },
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.selectedPatients = []
        this.fetchFirstPage()
      }
    },
  },
  mounted () {
    this.fetchCustomFields()
    this.fetchSegments(true)
    if (!this.storedTabs && !this.storedFilters) {
      this.fetchCustomers()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchCustomers() {
      this.isFetching = true
      let endpoint = `/connect/${this.company.slug}/customers`
      let params = {
        page_size: this.pageSize,
        page: this.currentPage
      }
      if (this.searchText) {
        params.search = this.searchText
      }
      if (this.selectedSegment) {
        if (this.selectedSegment.dynamic && this.selectedSegment.filter_params) {
          endpoint += `?${this.selectedSegment.filter_params}`
        } else {
          params.groups = this.selectedSegment.id
        }
      }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(endpoint, { params })
        .then(resp => {
          let data = resp.data
          let customers = data.results
          customers.map(item => {
            item.checked = false
            if (this.previousCustomers) {
              if (this.previousCustomers.includes(item.id)) {
                item.checked = true
              }
            }
            if (!item.age && item.dob) {
              item.age = this.getAge(item.dob)
            }
          })
          this.customers = customers
          this.totalCount = data.count
        })
        .catch(err => {
          let message = err.response?.data?.detail
          if (message.includes('Invalid page')) {
            this.fetchFirstPage()
          }
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchCustomFields() {
      let params = { page_size: -1 }
      this.$axios.get(`/clinics/${this.company.slug}/patient_custom_fields`, { params })
      .then(resp => {
        const results = resp.data?.results
        if (results) {
          const fields = []
          results.forEach(field => {
            if (field.status === 'active') {
              field.enable = false
              fields.push(field)
            }
          })
          this.$store.commit('clinic/setCustomFields', fields)
        }
      })
      .catch(() => {})
    },
    fetchSegments (isMounted) {
      this.isFetchingSegment = true
      this.segmentTab = 0
      this.segments = [{ id: 'all', title: 'All' }]
      const params = {
        page_size: -1,
        ordering: '-id',
        exclude: 'customer'
      }
      this.$axios.get(`/connect/${this.company.slug}/groups`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.segments = [{ id: 'all', title: 'All' }, ...resp.data.results]
          }
          if (isMounted) {
            this.checkedStored()
          } else {
            this.tabChanged()
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingSegment = false
        })
    },
    tabChanged () {
      if (this.segmentTab) {
        this.selectedSegment = this.segments[this.segmentTab]
      } else {
        this.selectedSegment = null
      }
      this.selectedPatients = []
      this.showDynamicFilters = false
      this.fetchFirstPage()
      this.checkMenu()
    },
    pageChanged () {
      this.fetchCustomers()
    },
    queryChanged: debounce(function () {
      this.fetchFirstPage()
    }, 750),
    checkedStored () {
      if (this.searchText) this.clickSearch()
      if (this.segmentTab) this.selectedSegment = this.segments[this.segmentTab]
      if (this.storedFilters) {
        this.$refs.filter.setFilters()
        this.checkMenu()
      } else {
        this.tabChanged()
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    successDeleteCustomer() {
      this.reset()
    },
    onChecked() {
      this.selectedPatients = this.customers.filter(item => item.checked === true)
      this.customers = [...this.customers]
      this.checkAll = false
    },
    onCheckedAll() {
      const clone = [...this.customers]
      clone.map(item => item.checked = this.checkAll)
      this.customers = [...clone]
      this.selectedPatients = this.customers.filter(item => item.checked === true)
    },
    clearSelection() {
      this.checkAll = false
      this.onCheckedAll()
    },
    onDelete() {
      this.openDeleteDialog = true
    },
    onAddSegment() {
      this.openAddToSegmentDialog = true
    },
    successAddCustomer() {
      this.reset()
    },
    successRemoveSegmentCustomer() {
      this.reset()
    },
    updateDynamicFilter () {
      this.reset()
      this.segments[this.segments.findIndex(f => f.id === this.selectedSegment.id)] = this.selectedSegment
      this.showDynamicFilters = false
    },
    reset () {
      this.checkAll = false
      this.selectedPatients = []
      this.fetchFirstPage()
    },
    closed (action) {
      if (action === 'cancel') {
        // do nothing
      } else if (action === 'add') {
        this.reset()
      } else {
        this.fetchCustomers()
      }
      this.selectedPatient = null
    },
    openDynamicFilter () {
      this.filters = []
      this.showDynamicFilters = true
    },
    fetchFirstPage () {
      this.currentPage = 1
      this.fetchCustomers()
    },
    onChanged(i) {
      this.selectedAddContactOption = this.addContactOptions[i]
    },
    clickedRow (item) {
      item.checked = !item.checked
      this.onChecked()
    },
    onSendSms () {
      this.$store.commit('donation/selectDonors', this.selectedPatients)
      this.$router.push(`/admin/clinic/patients/send-message`)
    },
    onSendEmail () {
      this.$store.commit('donation/selectDonors', this.selectedPatients)
      this.$router.push(`/admin/clinic/patients/send-email`)
    },
    tabShowMenu (segment) {
      if (segment.id === 'all') {
        return false
      }
      if (this.segments[this.segmentTab].id === segment.id) {
        return true
      }
      return false
    },
    checkMenu () {
      this.showMenu = false
      this.$nextTick(() => {
        this.showMenu = true
      })
    },
    getFieldData(id, field) {
      const data = field[id]
      if (data) {
        return data
      }
      if (field.custom_data.length) {
        const custom = field.custom_data.find(i => `${i.id}` === id)
        if (custom) {
          return custom.value
        }
      }
      return ''
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    getAge(date) {
      var today = new Date();
      var birthDate = new Date(date);
      var age = today.getFullYear() - birthDate.getFullYear();
      var month = today.getMonth() - birthDate.getMonth();
      if (month < 0 ||(month === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
  }
}
</script>

<style scoped>
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
</style>